import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import IconAdd from "../../../dynamicicons/icon-add";
import IconRemove from "../../../dynamicicons/icon-remove";
import VelavuButton from "../../../elements/velavu-button";
import { makeStyleable, StyleableIcon } from "../../../helper/icon-helper";
import InventoryRow from "./inventory-row";
import { InventoryStateAsset } from "./inventory-state";
import InventoryStatusChip from "./inventory-status-chip";

export interface InventoryAssetRowProps {
	style?: React.CSSProperties;
	className?: string;
	asset: InventoryStateAsset;
}

enum ActionType {
	Add,
	Remove,
}

/**
 * An InventoryRow wrapper that takes an InventoryAsset and displays
 * the appropriate status and action button
 */
export default function InventoryAssetRow(props: InventoryAssetRowProps) {
	const { t } = useTranslation();

	//Resolve the type of action button to show
	const actionType = useMemo((): ActionType => {
		switch (props.asset.status.type) {
			case "confirmed":
			case "missing":
				return ActionType.Remove;
			default:
				return ActionType.Add;
		}
	}, [props.asset.status]);

	const actionTooltip = useMemo((): string => {
		switch (props.asset.status.type) {
			case "confirmed":
			case "missing":
				return t("inventory.actions.unassign");
			case "unassigned":
				if (props.asset.status.belongsIn !== null) {
					return t("inventory.actions.reassign_here");
				} else {
					return t("inventory.actions.assign_here");
				}
		}
	}, [props.asset.status, t]);

	//Construct the action button
	let actionIcon: StyleableIcon;
	switch (actionType) {
		case ActionType.Add:
			actionIcon = makeStyleable(IconAdd);
			break;
		case ActionType.Remove:
			actionIcon = makeStyleable(IconRemove);
			break;
	}

	return (
		<InventoryRow
			style={props.style}
			className={props.className}
			name={props.asset.name}
			status={<InventoryStatusChip status={props.asset.status} />}
			action={
				<VelavuButton
					icon={actionIcon}
					tooltip={actionTooltip}
					variant="plain"
				/>
			}
		/>
	);
}
